import { buildRumId } from './lib/buildRumId';
import { buildRumUrl } from './lib/buildRumUrl';

type RUMSettings = Ya.Rum.RUMSettings;
type RumVars = Ya.Rum.RumVars;

/**
 * Параметры сервиса для инициализации счетчика.
 */
export type InitRumInlineOptions = {
    /**
     * Окружение приложения.
     */
    env: string;
    /**
     * UID пользователя.
     */
    uid: string;
    /**
     * Домен.
     */
    tld: string;
    /**
     * RequestID сессии.
     */
    reqid: string;
    /**
     * А/Б эксперименты из ABT.
     */
    slots: string[];
    /**
     * Регион пользователя.
     */
    region: string;
    /**
     * Сервис логирования в Rum.
     */
    service?: string;
    /**
     * Проект логирования в Rum.
     */
    project: string;
    /**
     * Платформа: desktop или touch.
     */
    platform: string;
    /**
     * Язык пользователя.
     */
    lang?: string;
};

/**
 * Инициализация инлайновой части Rum счетчика.
 *
 * @kind helper
 * @param params - Параметры сервиса для инициализации счетчика.
 * @returns undefined
 */
export function initRumInline(params: InitRumInlineOptions): void {
    require('@yandex-int/rum-counter/dist/inline/interface');
    require('@yandex-int/rum-counter/dist/inline/io');
    require('@yandex-int/rum-counter/dist/inline/longtask');
    require('@yandex-int/rum-counter/dist/bundle/send');
    require('./lib/deferred-send/inline');

    const { env, uid, tld, reqid, slots, region, service, project, platform, lang } = params;

    const settings: RUMSettings = {
        beacon: Boolean(navigator.sendBeacon),
        clck: `https://yandex.${tld}/clck/click`,
        reqid,
        slots,
        sendAutoResTiming: true,
        sendUrlInResTiming: true,
        sendCookie: true,
        sendClientUa: true,
        sendFirstRaf: true,
        // Включаем непрерывный сбор метрик CLS и INP в течение всей жизни страницы.
        enableContinuousCollection: true,
        // Поле не описано в типах rum-counter. Отключаем отправку FID, т.к. более не смотрим на эту метрику,
        // а также она может отправляться уже после перехода на другую страницу, что ломает логирование.
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- автоматический игнор в рамках DIRECT-265451. Исправьте при следующем редактировании при помощи type-assert, чтобы избежать возможных ложно-положительных проверок typecheck!
        // @ts-expect-error -- см. выше
        disableFID: true,
        observeAllInp: true,
    };

    const page = buildRumId({ project, service, platform });
    const url = buildRumUrl();

    const vars: RumVars = {
        rum_id: page,
        region,
        '-env': env,
        '-uid': uid,
        '-page': page,
        '-project': project,
        '-platform': platform,
        // Докидываем URL в '-additional', не используя поле '-url',
        // потому что поле '-url' не прорастает в ClickHouse таблицы.
        '-additional': JSON.stringify({ tld, page, url, lang }),
    };

    if (service) {
        vars['-service'] = service;
    }

    Ya.Rum.init(settings, vars);
}
